const { fetchJSON } = window.BAO.Utils

export class StoreSelector extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.countryOptionsHtml = ''
    this.languageOptionsHtml = ''
    this.hasCountryOptions = false
    this.languagesOnly = false

    // eslint-disable-next-line compat/compat
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })
    const countryParam = params.country
    const languagePrompt = params.language_prompt === 'true'
    const languageOptionsData = this.retrieveLanguageOptionData(countryParam)

    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }

    let shouldOpen = true

    // If user has already selected a country, no need to show modal.
    if (!this.noChoiceMade && !this.noLocationStored && countryParam == null) {
      shouldOpen = false
    }

    if (countryParam !== null) {
      this.setStorageFromURL(countryParam)
      shouldOpen = false
    }

    if (countryParam && languagePrompt && languageOptionsData) {
      this.open(countryParam, true, true)
    } else if (shouldOpen) {
      this.open()
    }
  }

  open (countryCode = false, showCloseButton = false, languagesOnly = false) {
    this.languagesOnly = languagesOnly

    if (countryCode === false) {
      fetchJSON(`/browsing_context_suggestions.json`, {
        method: 'GET',
      }).then(res => {
        this.setupCountrySwitcherModal(res.detected_values.country.handle)
      })
    } else {
      this.setupCountrySwitcherModal(countryCode)
    }

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEL => {
        if (showCloseButton) {
          closeEL.removeAttribute('disabled')
          closeEL.setAttribute('aria-hidden', false)
        } else {
          closeEL.setAttribute('disabled', true)
          closeEL.setAttribute('aria-hidden', true)
        }
      })
    }

    if (this.els?.countryOptionsContainer.exists) {
      this.els?.countryOptionsContainer.elements.forEach(containerEl => {
        if (countryCode && languagesOnly) {
          containerEl.setAttribute('aria-hidden', true)
        } else {
          containerEl.setAttribute('aria-hidden', false)
        }
      })
    }
  }

  forceRedirect () {
    fetchJSON(`/browsing_context_suggestions.json`, {
      method: 'GET',
    }).then(res => {
      const userCountry = res.detected_values.country.handle

      const searchParams = new URLSearchParams(window.location.search)
      const paramsObject = {}
      searchParams.forEach((value, key) => {
        paramsObject[key] = value
      })

      // Filter out parameters starting with "country"
      const filteredParamsObject = Object.fromEntries(
        Object.entries(paramsObject).filter(
          ([key]) => !key.startsWith('country'),
        ),
      )

      const filteredSearchParams = new URLSearchParams(filteredParamsObject)

      const searchParamsString = filteredSearchParams.toString()
      window.location.href = `${
        this.sites[this.countries[userCountry].site].url
      }${this.shopifyPathname}?country=${userCountry}&${searchParamsString}&${
        this.sites[this.countries[userCountry].site].currency
      }`
    })
  }

  connectedCallback () {
    super.connectedCallback()

    this.constructCountryOptionsHtml()
  }

  setupListeners () {
    super.setupListeners()

    this.onCountryToggleClick = this.onCountryToggleClick.bind(this)
    this.onLanguageToggleClick = this.onLanguageToggleClick.bind(this)
    this.onConfirmClick = this.onConfirmClick.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)

    if (this.els?.countryToggle.exists) {
      this.els.countryToggle.elements.forEach(toggleEl => {
        this.listeners.add(toggleEl, 'click', this.onCountryToggleClick)
      })
    }

    if (this.els?.languageToggle.exists) {
      this.els.languageToggle.elements.forEach(toggleEl => {
        this.listeners.add(toggleEl, 'click', this.onLanguageToggleClick)
      })
    }

    if (this.els?.confirm.exists) {
      this.els.confirm.elements.forEach(selectEl => {
        this.listeners.add(selectEl, 'click', this.onConfirmClick)
      })
    }

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEL => {
        this.listeners.add(closeEL, 'click', this.onCloseClick)
      })
    }
  }

  setStorageFromURL (countryCode) {
    window.localStorage.setItem(this.storageName, 'true')
    window.localStorage.setItem(this.userLocationName, countryCode)
  }

  onCountryToggleClick () {
    if (this.els?.countryOptions.exists) {
      this.els.countryOptions.elements.forEach(optionsEl => {
        optionsEl.setAttribute(
          'aria-expanded',
          (optionsEl.getAttribute('aria-expanded') === 'false').toString(),
        )
      })
    }
    this.classList.toggle('sec-StoreSelector-expanded')
  }

  onLanguageToggleClick () {
    if (this.els?.languageOptions.exists) {
      this.els.languageOptions.elements.forEach(optionsEl => {
        optionsEl.setAttribute(
          'aria-expanded',
          (optionsEl.getAttribute('aria-expanded') === 'false').toString(),
        )
      })
    }
    this.classList.toggle('sec-StoreSelector-expanded')
  }

  onCountryOptionClick (e) {
    const selectedOptionValue = e.currentTarget.value
    const selectedOptionCode = e.currentTarget.dataset.code
    const selectedOptionCountry = e.currentTarget.dataset.country
    const selectedOptionCurrency = e.currentTarget.dataset.currency

    this.els.countryToggle.elements.forEach(toggleEl => {
      toggleEl.querySelector(
        '.sec-StoreSelector_CountryToggleText',
      ).innerHTML = `${selectedOptionCountry} / ${selectedOptionCurrency}`
      toggleEl.dataset.value = selectedOptionValue
      toggleEl.dataset.code = selectedOptionCode
    })

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.url = this.sites[selectedOptionValue].url
      confirmEl.dataset.code = selectedOptionCode
      confirmEl.dataset.currency = this.sites[selectedOptionValue].currency
      confirmEl.innerHTML = this.dataset.changeLabel
    })

    if (this.els?.countryOptions.exists) {
      this.els.countryOptions.elements.forEach(optionsEl => {
        optionsEl.setAttribute('aria-expanded', 'false')
      })
    }
    this.classList.remove('sec-StoreSelector-expanded')

    this.toggleLanguageOptionList(selectedOptionCode)
  }

  onLanguageOptionClick (e) {
    const selectedOptionValue = e.currentTarget.value
    const selectedOptionTitle = e.currentTarget.dataset.languageTitle

    window.dataLayer.push({
      event: 'languageSelected',
      language: selectedOptionTitle,
    })

    this.els.languageToggle.elements.forEach(toggleEl => {
      toggleEl.querySelector(
        '.sec-StoreSelector_LanguageToggleText',
      ).innerHTML = `${selectedOptionTitle}`
      toggleEl.dataset.value = selectedOptionValue
      toggleEl.dataset.title = selectedOptionTitle
    })

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.subdirectory = selectedOptionValue
    })

    if (this.els?.languageOptions.exists) {
      this.els.languageOptions.elements.forEach(optionsEl => {
        optionsEl.setAttribute('aria-expanded', 'false')
      })
    }
    this.classList.remove('sec-StoreSelector-expanded')
  }

  onConfirmClick (event) {
    event.preventDefault()

    window.localStorage.setItem(this.storageName, 'true')
    window.localStorage.setItem(
      this.userLocationName,
      event.currentTarget.dataset.code,
    )

    const searchParams = new URLSearchParams(window.location.search)
    const paramsObject = {}
    searchParams.forEach((value, key) => {
      paramsObject[key] = value
    })

    // Filter out parameters starting with "country"
    const filteredParamsObject = Object.fromEntries(
      Object.entries(paramsObject).filter(([key]) => !key.startsWith('country')),
    )

    const filteredSearchParams = new URLSearchParams(filteredParamsObject)
    const searchParamsString = filteredSearchParams
      .toString()
      .replace('language_prompt=true', '')

    let subdirectory = ''
    if (event.currentTarget.dataset.subdirectory) {
      subdirectory = `/${event.currentTarget.dataset.subdirectory}`
    }

    if (event.currentTarget.hasAttribute('data-url')) {
      const href = `${event.currentTarget.dataset.url}${subdirectory}${this.shopifyPathname}?country=${event.currentTarget.dataset.code}&${searchParamsString}&${event.currentTarget.dataset.currency}`
      window.location.href = href
    }

    document.body.classList.remove('store-selector-open')
  }

  onCloseClick (event) {
    event.preventDefault()

    window.localStorage.setItem(this.storageName, 'true')
    window.localStorage.setItem(this.userLocationName, this.dataset.store)

    this.setAttribute('aria-hidden', 'true')

    if (document.body.classList.contains('util-DrawersOpen')) return
    document.body.classList.remove('util-SiteOverflowed')
    document.body.classList.remove('store-selector-open')
  }

  setupCountrySwitcherModal (countryCode) {
    // If user country matches store country, no need to show modal.
    if (countryCode === this.dataset.store) {
      window.localStorage.setItem(this.storageName, 'true')
      window.localStorage.setItem(this.userLocationName, countryCode)
      return
    }

    this.createCountryOptionsList(countryCode)
    this.setDefaultConfirmButtonURL()
    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('util-SiteOverflowed')
    document.body.classList.add('store-selector-open')
  }

  constructCountryOptionsHtml () {
    if (this.hasCountryOptions) return

    const countryCode = window.localStorage.getItem(this.userLocationName)

    Object.entries(this.countries).forEach(([key, value]) => {
      const { currency, name, site } = value
      const isCurrentSite = key.toLowerCase() === countryCode?.toLowerCase()

      this.countryOptionsHtml += this.createCountryOptionElement(
        key,
        site,
        isCurrentSite,
        name,
        currency,
      )
    })

    this.hasCountryOptions = true
  }

  createCountryOptionsList (countryCode) {
    if (this.els?.countryOptions.exists) {
      this.els.countryOptions.element.insertAdjacentHTML(
        'beforeend',
        this.countryOptionsHtml,
      )
    }

    this.setCountryValue(countryCode)

    document
      .querySelectorAll('.sec-StoreSelector_CountryOption')
      .forEach(option =>
        option.addEventListener('click', e => {
          this.onCountryOptionClick(e)
        }),
      )
  }

  createCountryOptionElement (code, site, isCurrentSite, country, currency) {
    return `<button
        class="sec-StoreSelector_CountryOption${
          isCurrentSite ? ' sec-StoreSelector_CountryOption-selected' : ''
        }"
        data-store-selector-el="countryOption"
        data-code="${code}"
        value="${site}"
        data-country="${country}"
        data-currency="${currency}"
        type="button"
      >
        <span class="sec-StoreSelector_CountryOptionCountry">${country}</span>
        <span class="sec-StoreSelector_CountryOptionCurrency">${currency}</span>
      </button>`
  }

  // Show the current country in the dropdown toggle element. E.g. the 'selected' country.
  setCountryValue (countryCode) {
    Object.entries(this.countries).forEach(([key, value]) => {
      const { currency, name, site } = value
      const isCurrentSite = key.toLowerCase() === countryCode.toLowerCase()

      if (isCurrentSite) {
        if (this.els?.countryToggle.exists) {
          this.els.countryToggle.elements.forEach(toggleEl => {
            toggleEl.querySelector(
              '.sec-StoreSelector_CountryToggleText',
            ).innerHTML = `${name} / ${currency}`
            toggleEl.dataset.value = site
            toggleEl.dataset.code = key
          })
        }

        this.toggleLanguageOptionList(key)
      }
    })
  }

  setDefaultConfirmButtonURL () {
    const selectedOptionValue = this.els.countryToggle.element.dataset.value
    const selectedOptionCode = this.els.countryToggle.element.dataset.code

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.url = this.sites[selectedOptionValue].url
      confirmEl.dataset.code = selectedOptionCode
      confirmEl.dataset.currency = this.sites[selectedOptionValue].currency
    })
  }

  toggleLanguageOptionList (key) {
    const countryCode = key.toUpperCase()
    const languageOptionsData = this.retrieveLanguageOptionData(countryCode)

    if (languageOptionsData) {
      this.constructLanguageOptionsHtml(languageOptionsData)
    } else {
      this.hideLanguageOptions()
    }

    let messageType
    if (this.languagesOnly === false && languageOptionsData !== false) {
      messageType = 'store-and-language'
    } else if (this.languagesOnly === true && languageOptionsData !== false) {
      messageType = 'language'
    } else {
      messageType = 'store'
    }
    // Accepts 'store', 'language' and 'store-and-language'
    this.updateMessage(messageType)
  }

  constructLanguageOptionsHtml (languageOptionsData) {
    if (!this.els?.languageOptionsContainer.exists) return

    this.els.languageOptionsContainer.element.removeAttribute('aria-hidden')

    // Clear previous values
    this.languageOptionsHtml = ''
    if (this.els?.languageOptions.exists) {
      this.els.languageOptions.element.innerHTML = ''
    }

    languageOptionsData.forEach((item, index) => {
      this.languageOptionsHtml += this.createLanguageOptionElement(
        item.title,
        item.subdirectory,
        index,
      )
    })

    this.createLanguageOptionsList(languageOptionsData)
  }

  createLanguageOptionsList (languageOptionsData) {
    if (this.els?.languageOptions.exists) {
      this.els.languageOptions.element.insertAdjacentHTML(
        'beforeend',
        this.languageOptionsHtml,
      )
    }

    document
      .querySelectorAll('.sec-StoreSelector_LanguageOption')
      .forEach(option =>
        option.addEventListener('click', e => {
          this.onLanguageOptionClick(e)
        }),
      )

    this.setLanguageValue(languageOptionsData)
  }

  createLanguageOptionElement (title, subdirectory, index) {
    return `<button
        class="sec-StoreSelector_LanguageOption${
          index === 0 ? ' sec-StoreSelector_LanguageOption-selected' : ''
        }"
        data-store-selector-el="languageOption"
        data-language-title="${title}"
        value="${subdirectory}"
        type="button"
      >
        <span class="sec-StoreSelector_LanguageOptionCountry">${title}</span>
      </button>`
  }

  hideLanguageOptions () {
    if (this.els?.languageOptionsContainer.exists) {
      this.els.languageOptionsContainer.element.setAttribute(
        'aria-hidden',
        true,
      )
    }

    if (this.els?.languageOptions.exists) {
      this.els.languageOptions.element.innerHTML = ''
    }

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.removeAttribute('data-subdirectory')
    })

    this.languageOptionsHtml = ''
  }

  setLanguageValue (languageOptionsData) {
    const { title, subdirectory } = languageOptionsData[0]

    if (this.els?.languageToggle.exists) {
      this.els.languageToggle.elements.forEach(toggleEl => {
        toggleEl.querySelector(
          '.sec-StoreSelector_LanguageToggleText',
        ).innerHTML = `${title}`
        toggleEl.dataset.title = title
        toggleEl.dataset.subdirectory = subdirectory
      })
    }

    this.els.confirm.elements.forEach(confirmEl => {
      confirmEl.dataset.subdirectory = languageOptionsData[0].subdirectory
    })
  }

  updateMessage (type) {
    // Accepts 'store', 'language' and 'store-and-language'

    if (!this.els?.storeMessage.exists) return

    this.els?.storeMessage.elements.forEach(messageContainerEl => {
      const storeMessage = messageContainerEl.querySelector(
        '[data-store-selector-message="store"]',
      )
      const storeAndLanguageMessage = messageContainerEl.querySelector(
        '[data-store-selector-message="store-and-language"]',
      )
      const languageMessage = messageContainerEl.querySelector(
        '[data-store-selector-message="language"]',
      )

      if (!storeMessage || !storeAndLanguageMessage || !languageMessage) return

      if (type === 'store') {
        storeMessage.setAttribute('aria-hidden', false)
        storeAndLanguageMessage.setAttribute('aria-hidden', true)
        languageMessage.setAttribute('aria-hidden', true)
      } else if (type === 'language') {
        storeMessage.setAttribute('aria-hidden', true)
        storeAndLanguageMessage.setAttribute('aria-hidden', true)
        languageMessage.setAttribute('aria-hidden', false)
      } else if (type === 'store-and-language') {
        storeMessage.setAttribute('aria-hidden', true)
        storeAndLanguageMessage.setAttribute('aria-hidden', false)
        languageMessage.setAttribute('aria-hidden', true)
      }
    })
  }

  retrieveLanguageOptionData (key) {
    if (!key) return false

    key = key.toUpperCase()

    if (
      this.sites[key] &&
      this.sites[key].languages &&
      this.sites[key].languages.length > 1
    ) {
      return this.sites[key].languages
    } else {
      return false
    }
  }

  get shopifyPathname () {
    // Get pathname, check the first part (i.e. /pages), and make sure that it matches the expected
    // Shopify pathnames. If it doesn't, we assume it's a market subdirectory and remove it.
    const url = new URL(window.location.href)

    let pathname = url.pathname
    const pathnameSplit = pathname.split('/')
    if (!this.shopifyPathnames.includes(pathnameSplit[1])) {
      pathname = pathname.replace(`/${pathnameSplit[1]}`, '')
    }

    return pathname
  }

  get currentCountryList () {
    return this.getAttribute('switcher-country-list')
  }

  get noChoiceMade () {
    return (
      window.localStorage.getItem(this.storageName) == null ||
      (window.localStorage.getItem(this.storageName) === undefined &&
        !Shopify.designMode)
    )
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !Shopify.designMode)
    )
  }

  get storageName () {
    return `BAO-store-portrait`
  }

  get userLocationName () {
    return `BAO-location-portrait`
  }

  get sites () {
    return window.theme.sites
  }

  get countries () {
    return window.theme.countries
  }

  get shopifyPathnames () {
    return [
      'pages',
      'products',
      'collections',
      'blogs',
      'policies',
      'search',
      'account',
      'cart',
      '404',
    ]
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector)
}
